import { getApiEndpoints } from '@/services/api/endpoints';
import { selectCurrentUserProfile } from '@/store/selectors/authorizationSelectors';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import { isNotFoundError } from '@/lib';
import { DEFAULT_INVITATION_TYPE_NAME, InvitationDeliveryStatus, type IInvitation } from '@/domain/entities/invitation';
import type {
  CreateInvitationInput,
  CreateInvitationRequest,
  CreateInvitationResult,
  FetchInvitationsInput,
  InvitationAcceptRequest,
  InvitationAcceptResponse,
  InvitationMutationResult,
  InvitationVerifyResponse,
  ResendInvitationResult,
} from './invitationTypes';

type InvitationApiRecord = Partial<Omit<IInvitation, 'organizationID' | 'status' | 'deliveryStatus'>> & {
  id: string;
  email: string;
  expiresAt: number;
  organizationID?: string;
  orgId?: string;
  status?: string;
  deliveryStatus?: string;
};

interface InvitationsResponse {
  invitations: InvitationApiRecord[];
  count?: number;
}

interface ApiEnvelope<T> {
  message?: string;
  data: T;
}

type InvitationMutationApiResponse = InvitationApiRecord & {
  emailSent?: boolean;
  deliveryMessage?: string;
};

type LegacyInvitationAcceptResponse = {
  success: boolean;
  message?: string;
  data?: Omit<InvitationAcceptResponse, 'success' | 'message' | 'roleKey'> & {
    roleKey?: string;
  };
};

function isInvitationsResponse(response: InvitationApiRecord[] | InvitationsResponse): response is InvitationsResponse {
  return response && typeof response === 'object' && 'invitations' in response;
}

function hasDataEnvelope<T>(response: T | ApiEnvelope<T>): response is ApiEnvelope<T> {
  return !!response && typeof response === 'object' && 'data' in response;
}

function normalizeInvitationReason(reason?: string): InvitationVerifyResponse['reason'] {
  const normalized = reason?.trim().toLowerCase();
  if (normalized === 'expired' || normalized === 'accepted' || normalized === 'not_found') {
    return normalized;
  }
  if (normalized === 'revoked' || normalized === 'cancelled') {
    return 'revoked';
  }
  return 'not_found';
}

function normalizeVerifyInvitationResponse(response: InvitationVerifyResponse): InvitationVerifyResponse {
  if (!response.valid) {
    return {
      ...response,
      reason: normalizeInvitationReason(response.reason),
    };
  }

  if (response.reason) {
    return {
      ...response,
      reason: normalizeInvitationReason(response.reason),
    };
  }

  return response;
}

function parseErrorReason(error: Error & { body?: string }): InvitationVerifyResponse['reason'] | null {
  if (!error.body) return null;

  try {
    const payload = JSON.parse(error.body) as { reason?: string };
    return normalizeInvitationReason(payload.reason);
  } catch {
    return null;
  }
}

function buildQuery(params: Record<string, string | undefined>) {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    const trimmed = value?.trim();
    if (trimmed) searchParams.set(key, trimmed);
  }

  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

function normalizeInvitationStatus(status?: string): IInvitation['status'] {
  const normalized = status?.trim().toLowerCase();
  if (normalized === 'accepted' || normalized === 'expired') {
    return normalized;
  }
  if (normalized === 'revoked' || normalized === 'cancelled') {
    return 'revoked';
  }
  return 'pending';
}

function normalizeInvitationDeliveryStatus(deliveryStatus?: string): IInvitation['deliveryStatus'] | undefined {
  const normalized = deliveryStatus?.trim().toLowerCase();
  if (normalized === InvitationDeliveryStatus.Sent) return InvitationDeliveryStatus.Sent;
  if (normalized === InvitationDeliveryStatus.Failed) return InvitationDeliveryStatus.Failed;
  if (normalized === InvitationDeliveryStatus.Pending) return InvitationDeliveryStatus.Pending;
  return undefined;
}

function normalizeInvitation(invitation: InvitationApiRecord): IInvitation {
  const organizationID = invitation.organizationID ?? invitation.orgId ?? '';
  return {
    ...invitation,
    typeName: invitation.typeName ?? DEFAULT_INVITATION_TYPE_NAME,
    invitedBy: invitation.invitedBy ?? '',
    organizationID,
    status: normalizeInvitationStatus(invitation.status),
    deliveryStatus: normalizeInvitationDeliveryStatus(invitation.deliveryStatus),
  };
}

function getInviterNameFromProfile(profile: ReturnType<typeof selectCurrentUserProfile>): string {
  const inviterName = [profile?.firstName?.trim(), profile?.lastName?.trim()].filter(Boolean).join(' ').trim();
  return inviterName || profile?.email?.trim() || '';
}

function normalizeInvitationMutationResponse(payload: InvitationMutationApiResponse): InvitationMutationResult {
  const emailSent =
    payload.emailSent ?? normalizeInvitationDeliveryStatus(payload.deliveryStatus) === InvitationDeliveryStatus.Sent;
  const deliveryMessage = payload.deliveryMessage ?? payload.lastDeliveryError ?? undefined;

  return {
    invitation: normalizeInvitation(payload),
    emailSent,
    deliveryMessage,
  };
}

function normalizeAcceptInvitationResponse(
  response: InvitationAcceptResponse | ApiEnvelope<InvitationAcceptResponse> | LegacyInvitationAcceptResponse,
): InvitationAcceptResponse {
  if ('organizationID' in response) {
    return response;
  }

  if ('success' in response && response.data) {
    return {
      success: response.success,
      message: response.message,
      organizationID: response.data.organizationID,
      membershipID: response.data.membershipID,
      status: response.data.status,
      roleKey: response.data.roleKey ?? 'viewer',
    };
  }

  if ('success' in response) {
    throw new Error(response.message || 'Invitation acceptance response did not include membership details');
  }

  return response.data;
}

export const fetchInvitations = createAppAsyncThunk<IInvitation[], FetchInvitationsInput>(
  'invitations/fetchInvitations',
  async ({ organizationID, status }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = `${getApiEndpoints().core.invitations.getInvitations}${buildQuery({ organizationID, status })}`;
      const response = await apiService.get<InvitationApiRecord[] | InvitationsResponse>(endpoint);
      const invitations = isInvitationsResponse(response) ? response.invitations : response;
      return invitations.map(normalizeInvitation);
    } catch (err) {
      const error = err as Error;
      return rejectWithValue({ message: error.message });
    }
  },
);

export const getInvitation = createAppAsyncThunk<IInvitation, { invitationId: string }>(
  'invitations/getInvitation',
  async ({ invitationId }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = `${getApiEndpoints().core.invitations.getInvitation}${buildQuery({ id: invitationId })}`;
      const response = await apiService.get<InvitationApiRecord | ApiEnvelope<InvitationApiRecord>>(endpoint);
      const payload = hasDataEnvelope(response) ? response.data : response;
      return normalizeInvitation(payload);
    } catch (err) {
      const error = err as Error;
      return rejectWithValue({ message: error.message });
    }
  },
);

export const createInvitation = createAppAsyncThunk<CreateInvitationResult, CreateInvitationInput>(
  'invitations/createInvitation',
  async ({ email, organizationID, dryRun }, { extra: { config }, getState, rejectWithValue }) => {
    const { apiService } = config;

    const activeUserProfile = selectCurrentUserProfile(getState());
    if (!activeUserProfile) {
      return rejectWithValue({ message: 'User profile not found' });
    }

    const request: CreateInvitationRequest = {
      email,
      organizationID,
      inviterName: getInviterNameFromProfile(activeUserProfile),
      dryRun: dryRun ?? false,
    };

    try {
      const endpoint = getApiEndpoints().core.invitations.createInvitation;
      const response = await apiService.post<
        InvitationMutationApiResponse | ApiEnvelope<InvitationMutationApiResponse>
      >(endpoint, request);
      const payload = hasDataEnvelope(response) ? response.data : response;
      return normalizeInvitationMutationResponse(payload);
    } catch (err) {
      const error = err as Error;
      return rejectWithValue({ message: error.message });
    }
  },
);

export const deleteInvitation = createAppAsyncThunk<string, { invitationId: string }>(
  'invitations/deleteInvitation',
  async ({ invitationId }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = `${getApiEndpoints().core.invitations.deleteInvitation}${buildQuery({ id: invitationId })}`;
      await apiService.delete(endpoint);
      return invitationId;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue({ message: error.message });
    }
  },
);

export const hardDeleteInvitation = createAppAsyncThunk<string, { invitationId: string }>(
  'invitations/hardDeleteInvitation',
  async ({ invitationId }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = `${getApiEndpoints().core.invitations.hardDeleteInvitation}${buildQuery({ id: invitationId })}`;
      await apiService.delete(endpoint);
      return invitationId;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue({ message: error.message });
    }
  },
);

export const resendInvitation = createAppAsyncThunk<ResendInvitationResult, { invitationId: string }>(
  'invitations/resendInvitation',
  async ({ invitationId }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = getApiEndpoints().core.invitations.resendInvitation;
      const response = await apiService.put<InvitationMutationApiResponse | ApiEnvelope<InvitationMutationApiResponse>>(
        endpoint,
        { id: invitationId },
      );
      const payload = hasDataEnvelope(response) ? response.data : response;
      return normalizeInvitationMutationResponse(payload);
    } catch (err) {
      const error = err as Error;
      return rejectWithValue({ message: error.message });
    }
  },
);

export const verifyInvitationToken = createAppAsyncThunk<InvitationVerifyResponse, string>(
  'invitations/verifyInvitationToken',
  async (token, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = `${getApiEndpoints().core.invitations.verifyInvitation}${buildQuery({ token })}`;
      const response = await apiService.get<InvitationVerifyResponse>(endpoint, {
        authMode: 'none',
      });
      return normalizeVerifyInvitationResponse(response);
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      const reason = parseErrorReason(error);

      if (reason || isNotFoundError(error)) {
        return {
          valid: false,
          reason: reason ?? 'not_found',
        };
      }

      return rejectWithValue({ message: error.message });
    }
  },
);

export const acceptInvitation = createAppAsyncThunk<InvitationAcceptResponse, InvitationAcceptRequest>(
  'invitations/acceptInvitation',
  async (request, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = getApiEndpoints().core.invitations.acceptInvitation;
      const payload: InvitationAcceptRequest = {
        token: request.token,
        dryRun: request.dryRun ?? false,
      };
      const response = await apiService.post<
        InvitationAcceptResponse | ApiEnvelope<InvitationAcceptResponse> | LegacyInvitationAcceptResponse,
        InvitationAcceptRequest
      >(endpoint, payload);
      return normalizeAcceptInvitationResponse(response);
    } catch (err) {
      const error = err as Error;
      return rejectWithValue({ message: error.message || 'Failed to accept invitation' });
    }
  },
);
